import React from "react";
import { Line } from "react-chartjs-2";
import { Box, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";

// Same registration approach as the desktop graph (src/components/PerformanceGraph.jsx).
// chart.js v3 is tree-shakeable, so each build must register the pieces it uses.
// Registration is global + idempotent, so it's safe to call here too.
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Static (matches the Mulish tick font used by the desktop graph).
const TICK_FONT = {
  family: '"Mulish", sans-serif',
  weight: 700,
  size: 12,
};

/**
 * Loading placeholder for <LineGraph/> — one block at the chart's own height, so
 * the section doesn't resize when the data lands. Same approach as the desktop
 * `PerformanceGraphSkeleton`.
 */
export function LineGraphSkeleton({ height = 220 }) {
  return (
    <Box sx={{ width: "100%", height }}>
      <Skeleton variant="rectangular" width="100%" height="100%" />
    </Box>
  );
}

/**
 * LineGraph — reusable mobile line chart (chart.js / react-chartjs-2).
 *
 * Built separately from the desktop `PerformanceGraph` but in the same manner
 * (Line chart, y 0–100 step 25, "Attempt" x-axis, smooth tension). Tuned for
 * phones: fixed height instead of aspect ratio, dashed grid, amber series to
 * match the Figma. Drive it entirely through props so it can be reused for any
 * attempt/score series.
 *
 * @param {string[]|number[]} labels  x-axis labels (e.g. attempt numbers)
 * @param {number[]} data             y values (scores)
 * @param {string} [color]            series color (defaults to theme amber)
 * @param {string} [xLabel]           x-axis title (default "Attempt")
 * @param {number} [yMax]             y-axis max (default 100)
 * @param {number} [yStep]            y tick step (default 25)
 * @param {number} [height]           chart height in px (default 220)
 */
export default function LineGraph({
  labels = [],
  data = [],
  color,
  xLabel = "Attempt",
  yMax = 100,
  yStep = 25,
  height = 220,
}) {
  const theme = useTheme();

  const seriesColor = color || theme.palette.warning["clr-300"];
  const gridColor = theme.palette.neutral["clr-200"];
  const tickColor = theme.palette.neutral["clr-500"];

  const options = React.useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      elements: { line: { tension: 0.4 } },
      plugins: {
        title: { display: false },
        legend: { display: false },
        tooltip: { enabled: true },
      },
      scales: {
        y: {
          min: 0,
          max: yMax,
          ticks: { stepSize: yStep, color: tickColor, font: TICK_FONT },
          grid: { color: gridColor, borderDash: [4, 4], drawBorder: false },
        },
        x: {
          title: {
            display: true,
            text: xLabel,
            color: tickColor,
            font: { ...TICK_FONT, weight: 600 },
          },
          ticks: { color: tickColor, font: TICK_FONT },
          grid: { color: gridColor, borderDash: [4, 4], drawBorder: false },
        },
      },
      layout: { padding: 4 },
    }),
    [xLabel, yMax, yStep, gridColor, tickColor]
  );

  return (
    <Box sx={{ position: "relative", width: "100%", height }}>
      <Line
        options={options}
        data={{
          labels,
          datasets: [
            {
              data,
              borderColor: seriesColor,
              backgroundColor: seriesColor,
              pointBackgroundColor: seriesColor,
              pointBorderColor: seriesColor,
              borderWidth: 3,
              pointRadius: 5,
              pointHoverRadius: 7,
              fill: false,
            },
          ],
        }}
      />
    </Box>
  );
}
